import styled from "styled-components";
import {
  Card,
  IntroistLoader,
  IconTag,
  useTheme,
  Title,
  Layout,
  H4,
  Button
} from "@introist/react-foundation/v2";
import { useParams } from "react-router";
import { useEffect, useMemo, useState } from "react";
import {
  completePublicAccountConnect,
  failPublicAccountConnect,
  PublicAccountConnectDetails,
  startPublicAccountConnect
} from "./ConnectedAccountsApi";
import { useSearchParams } from "react-router-dom";

const Logo = styled.div<{ logo?: string }>`
  width: 40px;
  height: 40px;
  border-radius: var(--rounding-small);
  background-image: ${props => (props.logo ? `url(${props.logo})` : undefined)};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 99vh;
`;

const providers: { [key: string]: { name: string; description: string } } = {
  slack: {
    name: "Slack",
    description:
      "Connecting your account to this workspace authorizes Introist to send messages on behalf of you."
  },
  google: {
    name: "Google",
    description:
      "Connecting your account to this workspace authorizes Introist to send emails and schedule calendar events on behalf of you."
  },
  outlook: {
    name: "Outlook",
    description:
      "Connecting your account to this workspace authorizes Introist to send emails and schedule calendar events on behalf of you."
  },
  microsoft: {
    name: "Microsoft",
    description:
      "Connecting your Microsoft account to this workspace authorizes Introist to access the selected capabilities on behalf of you."
  }
};

export const StartPublicConnectRoute = () => {
  const { theme } = useTheme();

  const { workspaceToken } = useParams();
  const [search] = useSearchParams();
  const provider = search.get("provider");
  const capabilitiesParam = search.get("capabilities");
  const capabilities = useMemo(
    () => (capabilitiesParam ? capabilitiesParam.split(",") : undefined),
    [capabilitiesParam]
  );
  const providerDetails = provider ? providers[provider] : null;

  const [details, setDetails] = useState<PublicAccountConnectDetails | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    startPublicAccountConnect(workspaceToken!, provider!, capabilities)
      .then(setDetails)
      .catch(() => setError("Something failed"));
  }, [provider, workspaceToken, capabilities]);

  if (!details && !error) return <IntroistLoader globalCenter />;

  const renderReadyToConnect = () => {
    return (
      <Card elevated style={{ width: "400px" }}>
        <Layout.Group
          vertical
          alignItems="center"
          justifyContent="center"
          gap="xLarge"
          style={{ padding: theme.spacing.xLarge }}
        >
          <Logo logo={details?.workspace.logo} />
          <H4>
            Connect your {providerDetails?.name} account to {details?.workspace.name}
          </H4>
          <Title style={{ textAlign: "center" }}>{providerDetails?.description}</Title>
          <Button
            onClick={() => {
              window.location.replace(details?.authorizationUri!);
            }}
          >
            Connect
          </Button>
        </Layout.Group>
      </Card>
    );
  };

  return (
    <CenteredContent>
      {error && <ErrorCard title={error} />}
      {!error && renderReadyToConnect()}
    </CenteredContent>
  );
};

export const CompletePublicConnectRoute = () => {
  const { theme } = useTheme();

  const [search] = useSearchParams();
  const [error, setError] = useState<string | undefined>();
  const [completed, setCompleted] = useState<boolean>(false);

  useEffect(() => {
    const code = search.get("code");
    const state = search.get("state");

    if (!code || !state) {
      const oauthError = search.get("error");
      const oauthErrorDescription = search.get("error_description") ?? undefined;

      if (oauthError === "access_denied") {
        setError("Access was denied. Please try again and accept the required permissions.");
      } else if (oauthError === "consent_required") {
        setError("Admin approval is required to connect this account. Please contact your Microsoft administrator.");
      } else if (oauthError) {
        setError("Something went wrong while connecting your account. Please try again.");
      } else {
        setError("Invalid request. Please close this window and try connecting again.");
      }

      if (oauthError && state) {
        failPublicAccountConnect(state, oauthError, oauthErrorDescription).catch(() => {});
      }

      return;
    }

    completePublicAccountConnect(code, state)
      .then(() => setCompleted(true))
      .catch(() => setError("Failed to complete connecting account"));
  }, [search]);

  if (!completed && !error) return <IntroistLoader globalCenter />;

  if (error) {
    return (
      <CenteredContent>
        <ErrorCard title={error} />
      </CenteredContent>
    );
  }

  return (
    <CenteredContent>
      <Card elevated style={{ width: "400px" }}>
        <Layout.Group
          vertical
          alignItems="center"
          justifyContent="center"
          gap="xLarge"
          style={{ padding: theme.spacing.xLarge }}
        >
          <IconTag name="checkCircle" color={theme.palette.success} />
          <H4>Your account is now connected</H4>
          <Title style={{ textAlign: "center" }}>You can close this window.</Title>
        </Layout.Group>
      </Card>
    </CenteredContent>
  );
};

const ErrorCard = ({ title }: { title: string }) => {
  const { theme } = useTheme();

  return (
    <Card elevated style={{ width: "400px", height: "300px" }}>
      <Layout.Group
        vertical
        alignItems="center"
        justifyContent="center"
        style={{ height: "100%" }}
        gap="xLarge"
      >
        <IconTag color={theme.palette.danger} name="crossCircle" />
        <Title variant="bold">{title}</Title>
      </Layout.Group>
    </Card>
  );
};
